import { Badge } from '@/components/ui'
import type { IUserInfo } from '@/core/services/clients'
import { cn } from '@/lib/utils'

const NOT_SET = '—'

const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase()

/** The values typed into the form, when the panel is shown after a failed save. */
export interface EnteredUserValues {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
}

type ComparedRow = {
  label: string
  entered: string
  keycloak: string
  database: string
  /** Set when this side disagrees with the reference (the entered value, or IAM). */
  keycloakDiffers: boolean
  databaseDiffers: boolean
  /** Phone and email are the identity keys a duplicate is detected on — always emphasised. */
  isKey: boolean
}

const buildRows = (
  userInfo: IUserInfo,
  entered?: EnteredUserValues,
): ComparedRow[] => {
  const keycloak = userInfo.keycloakUserInfo
  const database = userInfo.databaseUserInfo

  const row = (
    label: string,
    enteredValue: string | null | undefined,
    keycloakValue: string | null | undefined,
    databaseValue: string | null | undefined,
    isKey = false,
  ): ComparedRow => {
    // With a form value present it is the reference — support needs to see how each stored
    // record deviates from what they just typed. Otherwise fall back to comparing the two
    // stored records against each other.
    const reference = entered ? enteredValue : keycloakValue
    const comparable = entered ? !!entered : !!keycloak
    return {
      label,
      entered: enteredValue || NOT_SET,
      keycloak: keycloakValue || NOT_SET,
      database: databaseValue || NOT_SET,
      isKey,
      keycloakDiffers:
        comparable &&
        !!keycloak &&
        normalize(keycloakValue) !== normalize(reference),
      databaseDiffers:
        comparable &&
        !!database &&
        normalize(databaseValue) !== normalize(reference),
    }
  }

  return [
    row(
      'First Name',
      entered?.firstName,
      keycloak?.firstName,
      database?.firstName,
    ),
    row('Last Name', entered?.lastName, keycloak?.lastName, database?.lastName),
    row('Phone Number', entered?.phone, keycloak?.phone, database?.phone, true),
    row('Email', entered?.email, keycloak?.email, database?.email, true),
  ]
}

export interface TroubleshootUserDetailsProps {
  userInfo: IUserInfo
  emptyMessage?: string
  /** Form values to compare the stored records against; adds an "Entered" column. */
  entered?: EnteredUserValues
  /**
   * Restricts the table to phone and email — the fields a create actually collides on.
   * Names are only noise there; the troubleshoot modal shows the full record.
   */
  keyFieldsOnly?: boolean
}

export const TroubleshootUserDetails = ({
  userInfo,
  emptyMessage = 'No user found in IAM or in the database.',
  entered,
  keyFieldsOnly = false,
}: TroubleshootUserDetailsProps): JSX.Element => {
  const foundNothing = !userInfo.keycloakUserInfo && !userInfo.databaseUserInfo

  if (foundNothing) {
    return (
      <span className="text-body-medium text-monochrome-1">{emptyMessage}</span>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-medium text-monochrome-2">User ID</span>
        <span className="text-body-medium text-monochrome-1">
          {userInfo.id || NOT_SET}
        </span>
        <Badge variant={userInfo.keycloakUserInfo ? 'success' : 'destructive'}>
          {userInfo.keycloakUserInfo ? 'In IAM' : 'Missing in IAM'}
        </Badge>
        <Badge variant={userInfo.databaseUserInfo ? 'success' : 'destructive'}>
          {userInfo.databaseUserInfo ? 'In Database' : 'Missing in Database'}
        </Badge>
        {userInfo.keycloakUserInfo && (
          <Badge
            variant={
              userInfo.keycloakUserInfo.isActive ? 'success' : 'destructive'
            }
          >
            {userInfo.keycloakUserInfo.isActive ? 'Active' : 'Inactive'}
          </Badge>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-monochrome-120">
        <table className="w-full min-w-[520px] text-body-medium">
          <thead>
            <tr className="text-monochrome-2">
              <th className="p-3 text-left font-normal">Field</th>
              {entered && (
                <th className="p-3 text-left font-normal text-monochrome-1">
                  Entered
                </th>
              )}
              <th className="p-3 text-left font-normal">IAM</th>
              <th className="p-3 text-left font-normal">Database</th>
            </tr>
          </thead>
          <tbody>
            {buildRows(userInfo, entered)
              .filter((row) => !keyFieldsOnly || row.isKey)
              .map((row) => {
                const rowDiffers = row.keycloakDiffers || row.databaseDiffers
                // Highlighting means one thing only: this value disagrees. Nothing is
                // emphasised just for being a phone or an email.
                const valueClass = (differs: boolean) =>
                  cn(
                    'p-3 break-all',
                    differs
                      ? 'text-accent-3 font-semibold'
                      : 'text-monochrome-1',
                  )
                return (
                  <tr
                    key={row.label}
                    className={cn(
                      'border-t border-monochrome-120',
                      rowDiffers && 'bg-accent-3/10',
                    )}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-monochrome-2">{row.label}</span>
                        {rowDiffers && (
                          <Badge variant="destructive">Differs</Badge>
                        )}
                      </div>
                    </td>
                    {entered && (
                      <td className={valueClass(rowDiffers)}>{row.entered}</td>
                    )}
                    <td className={valueClass(row.keycloakDiffers)}>
                      {row.keycloak}
                    </td>
                    <td className={valueClass(row.databaseDiffers)}>
                      {row.database}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
